/**
 * /care-library — the caregiver-facing magnet library.
 *
 * Strategy (per `/app/memory/VIDEO_CURATION_PROMPT.md` §8):
 *   - Lead magnet, lightly-gated. Anonymous users can browse the
 *     catalog; AI Navigator and "find a clinician" CTAs prompt sign-in.
 *   - Every video card renders the EXACT attribution string from the
 *     catalog row. Federal sources with `image_caveat=true` render the
 *     image-copyright disclaimer beneath the player.
 *   - The CareLibraryNavigator is the §8 differentiator — caregiver
 *     types tonight's problem in plain English, AI routes to the best
 *     3-minute clip + a clinician-marketplace question.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Brain, Sparkles, ChevronRight, Loader2, Search, ShieldCheck,
  Video, ArrowRight, Stethoscope, Heart,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { AISafetyBadge } from '../components/brand/AISafetyBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_LABELS = {
  'orientation': 'New caregiver',
  'safe-physical-care': 'Safe physical care',
  'medication-management': 'Medications',
  'dementia': 'Dementia & memory care',
  'caregiver-self-care': 'Caregiver self-care',
  'nutrition': 'Nutrition & hydration',
  'home-safety': 'Home safety',
  'end-of-life': 'End-of-life care',
  'care-navigation': 'Care navigation',
  'hospice-navigation': 'Hospice navigation',
};

export default function CareLibraryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTopic, setActiveTopic] = useState('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/care-library/items?audience=caregiver`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const topics = useMemo(() => {
    const set = new Set(items.map((i) => i.topic));
    return Array.from(set);
  }, [items]);

  const filtered = useMemo(() => {
    let xs = items;
    if (activeTopic !== 'all') xs = xs.filter((i) => i.topic === activeTopic);
    if (query.trim()) {
      const q = query.toLowerCase();
      xs = xs.filter((i) =>
        i.title.toLowerCase().includes(q) ||
        i.source_org.toLowerCase().includes(q) ||
        (i.navigator_keywords || []).some((k) => k.toLowerCase().includes(q))
      );
    }
    return xs;
  }, [items, activeTopic, query]);

  return (
    <div className="min-h-screen bg-cn-bg dark:bg-cn-ink-950" data-testid="care-library-page">
      {/* Hero + AI Navigator */}
      <div className="border-b border-cn-border bg-gradient-to-br from-emerald-50/60 via-white to-sky-50/40 dark:from-cn-ink-900 dark:via-cn-ink-950 dark:to-cn-ink-900">
        <div className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
          <Link to="/" className="text-xs text-cn-text-muted hover:text-cn-primary">← Back to CaraLoom</Link>
          <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 items-center justify-center mt-5 mb-4">
            <Heart className="w-6 h-6" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-cn-text dark:text-white leading-tight">
            Care, on demand. Clinically reviewed.
          </h1>
          <p className="text-base sm:text-lg text-cn-text-muted mt-4 max-w-3xl">
            A growing library of caregiver videos curated from the NIH, CDC,
            CMS, and the VA — public-domain, fully captioned, paired with a
            CaraLoom clinician you can reach when video isn&apos;t enough.
          </p>

          <CareLibraryNavigator items={items} />
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 items-center">
          <button
            type="button"
            onClick={() => setActiveTopic('all')}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${activeTopic === 'all' ? 'bg-cn-primary text-white border-cn-primary' : 'bg-white text-cn-text border-cn-border hover:border-cn-primary/40'}`}
            data-testid="topic-all"
          >
            All topics
          </button>
          {topics.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTopic(t)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${activeTopic === t ? 'bg-cn-primary text-white border-cn-primary' : 'bg-white text-cn-text border-cn-border hover:border-cn-primary/40'}`}
              data-testid={`topic-${t}`}
            >
              {TOPIC_LABELS[t] || t}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 bg-white border border-cn-border rounded-2xl px-2.5">
            <Search className="w-3.5 h-3.5 text-cn-text-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search library…"
              className="bg-transparent outline-none text-sm py-1.5 w-44"
              data-testid="library-search"
            />
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-16 text-cn-text-muted"><Loader2 className="w-5 h-5 inline-block animate-spin mr-2" /> Loading library…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-cn-text-muted">No videos match that filter yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            {filtered.map((item) => <VideoLibraryCard key={item.id} item={item} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/** Single video card. Renders attribution + image-caveat honestly. */
function VideoLibraryCard({ item }) {
  return (
    <div className="cn-card p-5 hover:shadow-lg transition-shadow" data-testid={`library-card-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center shrink-0">
          <Video className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">{TOPIC_LABELS[item.topic] || item.topic}</div>
          <h3 className="font-display font-bold text-base mt-0.5 leading-tight">{item.title}</h3>
        </div>
      </div>
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="cn-btn cn-btn-primary w-full justify-center mt-4 text-sm"
        data-testid={`library-card-watch-${item.id}`}
      >
        Watch <ArrowRight className="w-4 h-4" />
      </a>
      <div className="mt-3 pt-3 border-t border-cn-border text-[11px] text-cn-text-muted space-y-1">
        <div className="font-semibold text-cn-text dark:text-white">{item.source_org}</div>
        <div>{item.attribution}</div>
        {item.image_caveat && (
          <div className="text-amber-700 dark:text-amber-300 italic">
            Some imagery in this video may be subject to additional third-party copyright; the spoken content and structure are U.S. federal public-domain works.
          </div>
        )}
        {item.languages?.length > 1 && (
          <div>Languages: {item.languages.join(' · ')}</div>
        )}
        {item.accreditation && (
          <div className="text-emerald-700">{item.accreditation}</div>
        )}
      </div>
      <Link to="/auth?intent=match" className="text-xs text-cn-primary font-semibold mt-3 inline-flex items-center hover:underline" data-testid={`library-card-clinician-${item.id}`}>
        Need hands-on help with this? Find a CaraLoom clinician
        <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

/** CareLibraryNavigator — the §8 AI differentiator. */
function CareLibraryNavigator({ items }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const matched = useMemo(
    () => (result?.best_item_id ? items.find((i) => i.id === result.best_item_id) : null),
    [result, items]
  );

  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/care-library/navigate`, { question: q });
      setResult(r.data);
    } catch (e) {
      // 401 = anonymous user; nudge to sign in instead of an error toast.
      if (e.response?.status === 401) {
        setError('Sign in to use the AI Navigator. (Library browse is open to everyone.)');
      } else {
        setError('We could not route your question right now — please browse the library directly.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 cn-card p-5 border-cn-primary/40 bg-white/80 backdrop-blur" data-testid="care-library-navigator">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-cn-primary">
        <Sparkles className="w-3.5 h-3.5" /> AI Navigator
        <AISafetyBadge surfaceId="carelibrary.navigator" compact />
      </div>
      <h2 className="font-display text-lg sm:text-xl font-bold text-cn-text dark:text-white mt-1.5">
        Tell me what&apos;s happening tonight — I&apos;ll route you to the right 3-minute clip.
      </h2>
      <div className="flex items-center gap-2 mt-3 bg-cn-bg border border-cn-border rounded-2xl p-1.5 focus-within:border-cn-primary/40">
        <Brain className="w-4 h-4 text-cn-primary ml-2" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && ask()}
          placeholder="e.g., Dad fell again tonight and I can't get him up"
          className="border-none bg-transparent flex-1 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
          data-testid="navigator-input"
          disabled={busy}
        />
        <Button
          type="button"
          onClick={ask}
          disabled={busy || !q.trim()}
          className="cn-btn cn-btn-primary"
          data-testid="navigator-ask-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Find me a clip'}
        </Button>
      </div>
      {error && <p className="text-xs text-amber-700 mt-2" data-testid="navigator-error">{error}</p>}
      {result && !error && (
        <div className="mt-4 cn-card p-4 bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-200/60" data-testid="navigator-result">
          {result.reason && <p className="text-sm text-cn-text dark:text-cn-bone-50">{result.reason}</p>}
          {matched ? (
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Recommended clip</div>
                <a href={matched.url} target="_blank" rel="noopener noreferrer" className="font-display font-bold text-cn-primary hover:underline" data-testid="navigator-result-title">
                  {matched.title}
                </a>
              </div>
              <Link to="/auth?intent=match" className="cn-btn cn-btn-ghost text-sm" data-testid="navigator-result-clinician">
                <Stethoscope className="w-4 h-4" /> Talk to a clinician
              </Link>
            </div>
          ) : (
            <p className="text-xs text-amber-700 mt-2">If this is a medical emergency — chest pain, stroke signs, severe bleeding — please call 911 now.</p>
          )}
          {result.marketplace_thread && (
            <div className="mt-3 pt-3 border-t border-emerald-200/40 text-xs text-cn-text-muted">
              <strong>Question for a CaraLoom clinician:</strong> {result.marketplace_thread}
            </div>
          )}
        </div>
      )}
      <div className="mt-3 flex items-center gap-1.5 text-[11px] text-cn-text-muted">
        <ShieldCheck className="w-3 h-3 text-emerald-600" /> Your question is PHI-scrubbed before any AI sees it.
      </div>
    </div>
  );
}
